<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { buildAssessmentAnswerPayload } from "#bb/lib/assessmentAnswers";

const logger = useLogger("Checkbox");

/** 1) Expect 'id' from the parent */
const props = defineProps<{ id: string }>();
const emit = defineEmits<{
  (e: "content-update", value: string[]): void;
  (e: "answer-error", payload: { id: string; error: unknown }): void;
}>();

/** 2) Get definition data from the content manager */
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id));

/** 3) Inject features (editor mode) */
const features = inject("render-features", { editor: false });

// 4. Define a context menu "Edit" command.
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Building block editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the options",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 4) Expose instance properties for runtime use */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Checkbox",
    icon: "lucide:list-checks",
  },
});

const { getAnswer, getCachedAnswer, setAnswer } = useAnswers();

// --- UPDATED HERE: initialize as an array, not a string ---
const answer = ref<string[]>([]);
const answerPath = computed(() => getResolvedDataPath(props.id).join("/"));

// Reactively mirror the shared answer cache so an answer made on any surface (the page or the AI
// chat) is reflected here live; getAnswer() ensures the cache has been loaded at least once.
watch(
  () => getCachedAnswer(props.id, answerPath.value)?.value,
  (value) => {
    answer.value = Array.isArray(value) ? [...value] : [];
  },
  { immediate: true },
);

onMounted(() => {
  if (props.id) {
    void getAnswer(props.id, answerPath.value);
  }
});

// Form-field validation wiring.
const { error, validate } = useFormField({
  id: props.id,
  componentId: ComponentDefinitionIds.CHECKBOX_GROUP,
  getValue: () => answer.value,
  getValidation: () => definitionData.value?.validation,
  inputType: "array",
});

const save = async () => {
  if (validate().ok === false || !props.id || !definitionData.value) {
    return;
  }

  try {
    await setAnswer(
      props.id,
      buildAssessmentAnswerPayload({
        block: definitionData.value,
        componentId: ComponentDefinitionIds.CHECKBOX_GROUP,
        value: answer.value,
        path: answerPath.value,
      }),
      { throwOnError: true },
    );
    emit("content-update", answer.value);
  } catch (error) {
    emit("answer-error", { id: props.id, error });
  }
};
</script>

<template>
  <div :class="cn('relative space-y-4', $attrs.class ?? '')">
    <fieldset
      :aria-label="definitionData.id"
      :aria-invalid="!!error"
      class="-space-y-px rounded-md bg-card text-card-foreground"
      :class="error ? 'ring-1 ring-destructive rounded-md' : ''"
      :disabled="$attrs.disabled"
    >
      <label
        v-for="option in definitionData.options"
        :key="option.id"
        :aria-label="option.name"
        class="group flex cursor-pointer border border-border bg-card p-4 transition-colors first:rounded-tl-md first:rounded-tr-md last:rounded-br-md last:rounded-bl-md focus:outline-none has-checked:relative has-checked:z-10 has-checked:border-primary has-checked:bg-primary/15"
      >
        <input
          :id="option.id"
          v-model="answer"
          @change="save"
          type="checkbox"
          :value="option.id"
          class="relative mt-0.5 size-4 shrink-0 appearance-none rounded-sm border border-input bg-background before:absolute before:inset-1 before:bg-primary-foreground not-checked:before:hidden checked:border-primary checked:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:border-input disabled:bg-muted disabled:opacity-50 disabled:before:bg-muted-foreground forced-colors:appearance-auto forced-colors:before:hidden"
        />
        <span class="ml-3 flex flex-col">
          <span class="block text-sm font-medium text-foreground">{{ option.name }}</span>
          <span class="block text-sm text-muted-foreground">{{ option.description }}</span>
        </span>
      </label>
    </fieldset>
    <p v-if="error" class="text-destructive text-sm">{{ error }}</p>
  </div>

</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.CHECKBOX_GROUP,
  editor: () => import("#bb/components/editor/assessment/checkbox.vue"),
  icon: "lucide:list-checks",
  type: "Checkbox Group",
  description: "Select multiple options from a list",
  default: {
    options: [
      { id: crypto.randomUUID(), name: "Option A", value: 1, description: "" },
      { id: crypto.randomUUID(), name: "Option B", value: 2, description: "" },
      { id: crypto.randomUUID(), name: "Option C", value: 3, description: "" },
    ],
    values: [],
  },
  categories: ["Assessment"],
  order: 10001,
};
</script>
